/**
 * A small INI parser.
 *
 * Written here rather than pulled in as a dependency because the grammar this
 * application needs is tiny — sections, `key = value`, and comments — and a
 * parser we own is a parser whose coercion rules we can state exactly. The
 * coercion is the part that matters: `encryptPayloads = false` has to arrive as
 * the boolean false, not the non-empty (and therefore truthy) string "false",
 * which is the classic way a security flag ends up permanently on.
 *
 * Grammar accepted:
 *   ; comment                 (also # comment)
 *   [section]
 *   key = value
 *   key = "value ; with a semicolon"
 *
 * Keys before any [section] land in the returned object's root. Later
 * definitions of the same key win, so an override appended to the end of a file
 * behaves the way somebody editing it would expect.
 */

/** Values that mean true/false, compared case-insensitively. */
const TRUE_VALUES = new Set(['true', 'yes', 'on', '1']);
const FALSE_VALUES = new Set(['false', 'no', 'off', '0']);

/**
 * Strips an unquoted trailing comment.
 *
 * Two rules keep this from eating real values:
 *
 *   - Markers inside quotes are ignored, so `"a ; b"` survives intact.
 *   - A marker only starts a comment if some non-whitespace has already
 *     appeared in the value. Without that, `brand = #0B6E99` parses as an empty
 *     value followed by a comment, and a colour becomes ''.
 *
 * The cost of the second rule is that a value consisting only of a comment
 * (`key = ; note`) is read as the literal text "; note". That is a far rarer
 * thing to write than a value beginning with '#'.
 */
function stripInlineComment(raw) {
  let inQuote = null;
  let seenValue = false;

  for (let i = 0; i < raw.length; i += 1) {
    const char = raw[i];

    if (inQuote) {
      if (char === inQuote) inQuote = null;
      continue;
    }

    if (char === '"' || char === "'") {
      inQuote = char;
      seenValue = true;
      continue;
    }

    if ((char === ';' || char === '#') && seenValue) {
      return raw.slice(0, i);
    }

    if (!/\s/.test(char)) seenValue = true;
  }

  return raw;
}

/** Removes one matching pair of surrounding quotes, if present. */
function unquote(value) {
  if (value.length >= 2) {
    const first = value[0];
    const last = value[value.length - 1];
    if ((first === '"' && last === '"') || (first === "'" && last === "'")) {
      return value.slice(1, -1);
    }
  }
  return value;
}

/**
 * Turns a raw value into a boolean, number or string.
 *
 * A quoted value is always left as a string — that is how you write the literal
 * text "true" or a numeric-looking identifier without it being coerced.
 */
function coerce(raw) {
  const trimmed = raw.trim();
  const unquoted = unquote(trimmed);

  if (unquoted !== trimmed) return unquoted;
  if (trimmed === '') return '';

  const lowered = trimmed.toLowerCase();
  if (TRUE_VALUES.has(lowered)) return true;
  if (FALSE_VALUES.has(lowered)) return false;

  // Number() accepts '' and whitespace, both already handled above.
  if (/^-?\d+(\.\d+)?$/.test(trimmed)) return Number(trimmed);

  return trimmed;
}

/**
 * Parses INI text into a plain object of sections.
 *
 * Throws on a line that is neither blank, a comment, a section header, nor a
 * key/value pair. A configuration file with a typo in it should fail loudly at
 * start-up rather than silently drop the setting somebody thought they had set.
 */
export function parseIni(text) {
  const result = {};
  let section = result;

  const lines = String(text).split(/\r?\n/);

  for (let index = 0; index < lines.length; index += 1) {
    const line = lines[index].trim();

    if (line === '' || line.startsWith(';') || line.startsWith('#')) continue;

    const sectionMatch = /^\[([^\]]+)\]$/.exec(line);
    if (sectionMatch) {
      const name = sectionMatch[1].trim();
      if (!result[name]) result[name] = {};
      section = result[name];
      continue;
    }

    const separator = line.indexOf('=');
    if (separator === -1) {
      throw new Error(`Invalid configuration on line ${index + 1}: "${lines[index]}"`);
    }

    const key = line.slice(0, separator).trim();
    if (key === '') {
      throw new Error(`Missing configuration key on line ${index + 1}`);
    }

    section[key] = coerce(stripInlineComment(line.slice(separator + 1)));
  }

  return result;
}
